import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useTranslate } from 'react-polyglot';
import { useQuery } from '@tanstack/react-query';
import { useAuth } from '../../../Auth';
import { useAxios } from '../../../AxiosHandler';
import {
    Chart as ChartJS,
    RadialLinearScale,
    PointElement,
    LineElement,
    Filler,
    Tooltip,
    Legend,
} from 'chart.js';
import { Radar } from 'react-chartjs-2';

import { Col, Container, Row } from 'react-bootstrap';

import styles from './SelectedOpenBreeding.module.scss';
import Card from '../../Shared/Card/Card';
import { Form } from 'react-bootstrap';
import { general } from '../../../general';
import PedigreeCard from '../Statistics/Cards/PedigreeCard/PedigreeCard';
import { usePedigree } from '../Statistics/Cards/PedigreeCard/usePedigree';

ChartJS.register(RadialLinearScale, PointElement, LineElement, Filler, Tooltip, Legend);

function SelectedOpenBreeding(props: SelectedOpenBreedingProps) {
    const t = useTranslate();
    const navigate = useNavigate();
    const auth = useAuth();
    const axiosHandler = useAxios();
    const axios = axiosHandler.axios;
    const params = useParams();

    const [breeding, setBreeding] = useState<any | undefined>();
    const [dam, setDam] = useState<any | undefined>();
    const [sire, setSire] = useState<any | undefined>();
    const [selectedReg, setSelectedReg] = useState<any | undefined>();

    const [loading, setLoading] = useState(false);
    const [searchText, setSearchText] = useState("");
    const [searchResults, setSearchResults] = useState<any | undefined>();

    const [foundSire, setFoundSire] = useState<any>();
    const [addingSire, setAddingSire] = useState(false);
    
    const [expectingMonth, setExpectingMonth] = useState<string>("");
    const [expectingYear, setExpectingYear] = useState<string>("");
    
    const [accessLinks, setAccessLinks] = useState<any[]>([]);
    const [generatingLink, setGeneratingLink] = useState(false);
    const [loadingLinks, setLoadingLinks] = useState(false);
    const [copiedKey, setCopiedKey] = useState<string | null>(null);
    const [sendingEmailGuid, setSendingEmailGuid] = useState<string | null>(null);
    const [emailInputs, setEmailInputs] = useState<Record<string, string>>({});
    const [simulationResult, setSimulationResult] = useState<any | null>(null);
    const [isSimulating, setIsSimulating] = useState<boolean>(false);

    const { data: puppyListingColumns } = useQuery({
        queryKey: ['breeding-columns-info', auth.selectedBreed],
        queryFn: () => axios.single({
            method: 'get',
            url: "/api/dogs/breedingcolumnsinfo/" + auth.selectedBreed,
            responseType: 'json',
        }).then((res: any) => res.data),
        enabled: !!auth.selectedBreed,
        staleTime: 5 * 60 * 1000,
        gcTime: 10 * 60 * 1000,
    });

    const [familyTreeGenerations, setFamilyTreeGenerations] = useState<number>(2);

    const { data: damDog } = useQuery({
        queryKey: ['dog', dam?.ANIMAL_ID],
        queryFn: () => axios.single({
            method: 'get',
            url: "/api/dogs/" + dam?.ANIMAL_ID,
            responseType: 'json',
            headers: { 'x-access-token': auth.user['token'], "selected-country": auth.selectedCountry || "all" }
        }).then((res: any) => res.data),
        enabled: !!dam?.ANIMAL_ID,
        staleTime: 5 * 60 * 1000,
        gcTime: 10 * 60 * 1000,
    });

    const { data: sireDog } = useQuery({
        queryKey: ['dog', sire?.ANIMAL_ID],
        queryFn: () => axios.single({
            method: 'get',
            url: "/api/dogs/" + sire?.ANIMAL_ID,
            responseType: 'json',
            headers: { 'x-access-token': auth.user['token'], "selected-country": auth.selectedCountry || "all" }
        }).then((res: any) => res.data),
        enabled: !!sire?.ANIMAL_ID,
        staleTime: 5 * 60 * 1000,
        gcTime: 10 * 60 * 1000,
    });

    const { data: damHdScore } = useQuery({
        queryKey: ['dog-hd', dam?.ANIMAL_ID],
        queryFn: () => axios.single({
            method: 'get',
            url: "/api/statistics/dog/" + dam?.ANIMAL_ID + "/health/hd/latest",
            responseType: 'json',
            headers: { 'x-access-token': auth.user['token'], "selected-country": auth.selectedCountry || "all" }
        }).then((res: any) => res.data),
        enabled: !!dam?.ANIMAL_ID,
        staleTime: 5 * 60 * 1000,
        gcTime: 10 * 60 * 1000,
    });

    const { data: sireHdScore } = useQuery({
        queryKey: ['dog-hd', sire?.ANIMAL_ID],
        queryFn: () => axios.single({
            method: 'get',
            url: "/api/statistics/dog/" + sire?.ANIMAL_ID + "/health/hd/latest",
            responseType: 'json',
            headers: { 'x-access-token': auth.user['token'], "selected-country": auth.selectedCountry || "all" }
        }).then((res: any) => res.data),
        enabled: !!sire?.ANIMAL_ID,
        staleTime: 5 * 60 * 1000,
        gcTime: 10 * 60 * 1000,
    });

    const { data: damPupsCount } = useQuery({
        queryKey: ['dog-pupscount', dam?.ANIMAL_ID],
        queryFn: () => axios.single({
            method: 'get',
            url: "/api/statistics/dog/" + dam?.ANIMAL_ID + "/pupscount",
            responseType: 'json',
            headers: { 'x-access-token': auth.user['token'], "selected-country": auth.selectedCountry || "all" }
        }).then((res: any) => res.data),
        enabled: !!dam?.ANIMAL_ID,
        staleTime: 5 * 60 * 1000,
        gcTime: 10 * 60 * 1000,
    });

    const { data: sirePupsCount } = useQuery({
        queryKey: ['dog-pupscount', sire?.ANIMAL_ID],
        queryFn: () => axios.single({
            method: 'get',
            url: "/api/statistics/dog/" + sire?.ANIMAL_ID + "/pupscount",
            responseType: 'json',
            headers: { 'x-access-token': auth.user['token'], "selected-country": auth.selectedCountry || "all" }
        }).then((res: any) => res.data),
        enabled: !!sire?.ANIMAL_ID,
        staleTime: 5 * 60 * 1000,
        gcTime: 10 * 60 * 1000,
    });

    const { data: damUseLeash } = useQuery({
        queryKey: ['dog-use-leash', dam?.ANIMAL_ID],
        queryFn: () => axios.single({
            method: 'get',
            url: "/api/statistics/dog/" + dam?.ANIMAL_ID + "/use/leash/count",
            responseType: 'json',
            headers: { 'x-access-token': auth.user['token'], "selected-country": auth.selectedCountry || "all" }
        }).then((res: any) => res.data?.[0]?.TESTS_COUNT ?? 0),
        enabled: !!dam?.ANIMAL_ID,
        staleTime: 5 * 60 * 1000,
        gcTime: 10 * 60 * 1000,
    });

    const { data: damUseLoose } = useQuery({
        queryKey: ['dog-use-loose', dam?.ANIMAL_ID],
        queryFn: () => axios.single({
            method: 'get',
            url: "/api/statistics/dog/" + dam?.ANIMAL_ID + "/use/loose/count",
            responseType: 'json',
            headers: { 'x-access-token': auth.user['token'], "selected-country": auth.selectedCountry || "all" }
        }).then((res: any) => res.data?.[0]?.TESTS_COUNT ?? 0),
        enabled: !!dam?.ANIMAL_ID,
        staleTime: 5 * 60 * 1000,
        gcTime: 10 * 60 * 1000,
    });

    const { data: damUseHare } = useQuery({
        queryKey: ['dog-use-hare', dam?.ANIMAL_ID],
        queryFn: () => axios.single({
            method: 'get',
            url: "/api/statistics/dog/" + dam?.ANIMAL_ID + "/use/hare/count",
            responseType: 'json',
            headers: { 'x-access-token': auth.user['token'], "selected-country": auth.selectedCountry || "all" }
        }).then((res: any) => res.data?.[0]?.TESTS_COUNT ?? 0),
        enabled: !!dam?.ANIMAL_ID,
        staleTime: 5 * 60 * 1000,
        gcTime: 10 * 60 * 1000,
    });

    // const { data: damUseBird } = useQuery({
    //     queryKey: ['dog-use-bird', dam?.ANIMAL_ID],
    //     queryFn: () => axios.single({
    //         method: 'get',
    //         url: "/api/statistics/dog/" + dam?.ANIMAL_ID + "/use/bird/count",
    //         responseType: 'json',
    //         headers: { 'x-access-token': auth.user['token'], "selected-country": auth.selectedCountry || "all" }
    //     }).then((res: any) => res.data?.[0]?.TESTS_COUNT ?? 0),
    //     enabled: !!dam?.ANIMAL_ID,
    //     staleTime: 5 * 60 * 1000,
    //     gcTime: 10 * 60 * 1000,
    // });

    // const { data: damUseBirdfetch } = useQuery({
    //     queryKey: ['dog-use-birdfetch', dam?.ANIMAL_ID],
    //     queryFn: () => axios.single({
    //         method: 'get',
    //         url: "/api/statistics/dog/" + dam?.ANIMAL_ID + "/use/birdfetch/count",
    //         responseType: 'json',
    //         headers: { 'x-access-token': auth.user['token'], "selected-country": auth.selectedCountry || "all" }
    //     }).then((res: any) => res.data?.[0]?.TESTS_COUNT ?? 0),
    //     enabled: !!dam?.ANIMAL_ID,
    //     staleTime: 5 * 60 * 1000,
    //     gcTime: 10 * 60 * 1000,
    // });

    const { data: sireUseLeash } = useQuery({
        queryKey: ['dog-use-leash', sire?.ANIMAL_ID],
        queryFn: () => axios.single({
            method: 'get',
            url: "/api/statistics/dog/" + sire?.ANIMAL_ID + "/use/leash/count",
            responseType: 'json',
            headers: { 'x-access-token': auth.user['token'], "selected-country": auth.selectedCountry || "all" }
        }).then((res: any) => res.data?.[0]?.TESTS_COUNT ?? 0),
        enabled: !!sire?.ANIMAL_ID,
        staleTime: 5 * 60 * 1000,
        gcTime: 10 * 60 * 1000,
    });

    const { data: sireUseLoose } = useQuery({
        queryKey: ['dog-use-loose', sire?.ANIMAL_ID],
        queryFn: () => axios.single({
            method: 'get',
            url: "/api/statistics/dog/" + sire?.ANIMAL_ID + "/use/loose/count",
            responseType: 'json',
            headers: { 'x-access-token': auth.user['token'], "selected-country": auth.selectedCountry || "all" }
        }).then((res: any) => res.data?.[0]?.TESTS_COUNT ?? 0),
        enabled: !!sire?.ANIMAL_ID,
        staleTime: 5 * 60 * 1000,
        gcTime: 10 * 60 * 1000,
    });

    const { data: sireUseHare } = useQuery({
        queryKey: ['dog-use-hare', sire?.ANIMAL_ID],
        queryFn: () => axios.single({
            method: 'get',
            url: "/api/statistics/dog/" + sire?.ANIMAL_ID + "/use/hare/count",
            responseType: 'json',
            headers: { 'x-access-token': auth.user['token'], "selected-country": auth.selectedCountry || "all" }
        }).then((res: any) => res.data?.[0]?.TESTS_COUNT ?? 0),
        enabled: !!sire?.ANIMAL_ID,
        staleTime: 5 * 60 * 1000,
        gcTime: 10 * 60 * 1000,
    });

    const { data: sireUseBird } = useQuery({
        queryKey: ['dog-use-bird', sire?.ANIMAL_ID],
        queryFn: () => axios.single({
            method: 'get',
            url: "/api/statistics/dog/" + sire?.ANIMAL_ID + "/use/bird/count",
            responseType: 'json',
            headers: { 'x-access-token': auth.user['token'], "selected-country": auth.selectedCountry || "all" }
        }).then((res: any) => res.data?.[0]?.TESTS_COUNT ?? 0),
        enabled: !!sire?.ANIMAL_ID,
        staleTime: 5 * 60 * 1000,
        gcTime: 10 * 60 * 1000,
    });

    // const { data: sireUseBirdfetch } = useQuery({
    //     queryKey: ['dog-use-birdfetch', sire?.ANIMAL_ID],
    //     queryFn: () => axios.single({
    //         method: 'get',
    //         url: "/api/statistics/dog/" + sire?.ANIMAL_ID + "/use/birdfetch/count",
    //         responseType: 'json',
    //         headers: { 'x-access-token': auth.user['token'], "selected-country": auth.selectedCountry || "all" }
    //     }).then((res: any) => res.data?.[0]?.TESTS_COUNT ?? 0),
    //     enabled: !!sire?.ANIMAL_ID,
    //     staleTime: 5 * 60 * 1000,
    //     gcTime: 10 * 60 * 1000,
    // });

    function getDogAge(dateBirth: string | null | undefined): string {
        if (!dateBirth) return "-";
        const birth = new Date(dateBirth);
        const now = new Date();
        let age = now.getFullYear() - birth.getFullYear();
        const m = now.getMonth() - birth.getMonth();
        if (m < 0 || (m === 0 && now.getDate() < birth.getDate())) age--;
        return age + " " + t("selectedrequestbreeding.results.fields.years");
    }

    function copyToClipboard(value: string, key: string) {
        navigator.clipboard.writeText(value ?? "");
        setCopiedKey(key);
        setTimeout(() => setCopiedKey(null), 1500);
    }

    function getAccessLinks() {
        if (!breeding?.ID || !auth.selectedBreed) return;
        setLoadingLinks(true);
        axios.single({
            method: 'get',
            url: `/api/dogs/puppylisting/accesslinks/${breeding.ID}/${auth.selectedBreed}`,
            responseType: 'json',
            headers: { 'x-access-token': auth.user['token'], "selected-country": auth.selectedCountry || "all" }
        }).then((res: any) => {
            setAccessLinks(res.data);
            const prefill: Record<string, string> = {};
            (res.data as any[]).forEach((link: any) => {
                prefill[link.ACCESSKEY] = link.DAM_EMAIL || "";
            });
            setEmailInputs(prev => ({ ...prefill, ...prev }));
        }).catch((err: any) => {
            console.log(err);
        }).finally(() => {
            setLoadingLinks(false);
        });
    }

    function getBreeding() {
        axios.single({
            method: 'get',
            url: "/api/breedings/" + params.breedingid,
            responseType: 'json',
            headers: { 'x-access-token': auth.user['token'], "selected-country": auth.selectedCountry || "all" }
        }).then((res: any) => {
            setBreeding(res.data);
            
            if (res.data.DAM_ANIMAL_ID != null) {
                setDam({
                    ANIMAL_ID: res.data.DAM_ANIMAL_ID,
                    NAME: res.data.DAM_ANIMAL_NAME,
                });
            }

            if (res.data.SIRE_ANIMAL_ID != null) {
                setSire({
                    ANIMAL_ID: res.data.SIRE_ANIMAL_ID,
                    NAME: res.data.SIRE_ANIMAL_NAME,
                });
            }

            if (res.data.SCHEMA_ID != null) {
                setSelectedReg(res.data.SCHEMA_DATA);
            }
        }).catch((err: any) => {
            console.log(err);
        });
    }

    function searchSingleSire() {
        setLoading(true);
        axios.single({
            method: 'post',
            url: "/api/dogs/searchdogs",
            responseType: 'json',
            data: {
                breedid: auth.selectedBreed,
                search: searchText,
                sex: "male",
            },
            headers: { 'x-access-token': auth.user['token'], "selected-country": auth.selectedCountry || "all" },
        }).then((res: any) => {
            setSearchResults(res.data);
        }).catch((err: any) => {
            console.log(err);
        }).finally(() => {
            setLoading(false);
        });
    }

    function toggleApprovedPublic(approved: string) {
        if (auth.selectedBreed == undefined) return;
        axios.single({
            method: 'post',
            url: "/api/breedings/" + breeding.ID + "/breedingapprovedpublish",
            responseType: 'json',
            headers: { 'x-access-token': auth.user['token'], "selected-country": auth.selectedCountry || "all" },
            data: {
                dogid: breeding.DAM_ANIMAL_ID,
                publish_public: approved
            }
        }).then((res) => {
            getBreeding();
        }).catch((err: any) => {
            console.log(err);
        });
    }

    // Will remove the sire from the breeding if dog is undefined
    function addSire(dog: any) {
        if (auth.selectedBreed == undefined) return;
        setAddingSire(true);
        axios.single({
            method: 'post',
            url: "/api/breedings/" + breeding.ID + "/addsire",
            responseType: 'json',
            headers: { 'x-access-token': auth.user['token'], "selected-country": auth.selectedCountry || "all" },
            data: {
                sireid: dog?.ANIMAL_ID,
            }
        }).then((res) => {
            getBreeding();
            if (dog == undefined) {
                setSire(undefined);
            }
        }).catch((err: any) => {
            console.log(err);
        }).finally(() => {
            setAddingSire(false);
        });
    }

    function setOpenBreedingStatus(status: string) {
        if (auth.selectedBreed == undefined) return;
        axios.single({
            method: 'post',
            url: "/api/breedings/" + breeding.ID + "/setbreedingstatus",
            responseType: 'json',
            headers: { 'x-access-token': auth.user['token'], "selected-country": auth.selectedCountry || "all" },
            data: {
                dogid: breeding.DAM_ANIMAL_ID,
                status: status
            }
        }).then((res) => {
            getBreeding();
        }).catch((err: any) => {
            console.log(err);
        });
    }

    function updateBreeding() {
        if (auth.selectedBreed == undefined) return;
        axios.single({
            method: 'post',
            url: "/api/breedings/" + breeding.ID + "/updatebreeding",
            responseType: 'json',
            headers: { 'x-access-token': auth.user['token'], "selected-country": auth.selectedCountry || "all" },
            data: {
                dogid: breeding.DAM_ANIMAL_ID,
                expecting_date: breeding.EXPECTING_DATE,
                birthdate: breeding.BIRTHDATE,
                malepups: breeding.MALEPUPS,
                femalepups: breeding.FEMALEPUPS,
                status: breeding.STATUS,
                freetext: breeding.FREETEXT,
                kennel_name: breeding.KENNEL_NAME,
            }
        }).then((res) => {
            getBreeding();
        }).catch((err: any) => {
            console.log(err);
        });
    }

    function generateAccessLink() {
        if (!breeding?.ID || !auth.selectedBreed) return;
        setGeneratingLink(true);
        axios.single({
            method: 'post',
            url: `/api/dogs/puppylisting/accesslink/${auth.selectedBreed}`,
            responseType: 'json',
            headers: { 'x-access-token': auth.user['token'], "selected-country": auth.selectedCountry || "all" },
            data: {
                breedingsId: breeding.ID
            }
        }).then((res: any) => {
            getAccessLinks();
        }).catch((err: any) => {
            console.log(err);
            alert(t("selectedopenbreeding.accesslinks.error"));
        }).finally(() => {
            setGeneratingLink(false);
        });
    }

    function deactivateAccessLink(guid: string) {
        if (!auth.selectedBreed) return;
        if (!window.confirm(t("selectedopenbreeding.accesslinks.confirmdeactivate"))) return;
        
        axios.single({
            method: 'delete',
            url: `/api/dogs/puppylisting/accesslink/${guid}/${auth.selectedBreed}`,
            responseType: 'json',
            headers: { 'x-access-token': auth.user['token'], "selected-country": auth.selectedCountry || "all" }
        }).then(() => {
            getAccessLinks();
        }).catch((err: any) => {
            console.log(err);
            alert(t("selectedopenbreeding.accesslinks.deactivateerror"));
        });
    }

    function activateAccessLink(guid: string) {
        if (!auth.selectedBreed) return;
        
        axios.single({
            method: 'put',
            url: `/api/dogs/puppylisting/accesslink/activate/${guid}/${auth.selectedBreed}`,
            responseType: 'json',
            headers: { 'x-access-token': auth.user['token'], "selected-country": auth.selectedCountry || "all" }
        }).then(() => {
            getAccessLinks();
        }).catch((err: any) => {
            console.log(err);
            alert(t("selectedopenbreeding.accesslinks.activateerror"));
        });
    }

    function hardDeleteAccessLink(guid: string) {
        if (!auth.selectedBreed) return;
        if (!window.confirm(t("selectedopenbreeding.accesslinks.confirmharddelete"))) return;
        
        axios.single({
            method: 'delete',
            url: `/api/dogs/puppylisting/accesslink/hard/${guid}/${auth.selectedBreed}`,
            responseType: 'json',
            headers: { 'x-access-token': auth.user['token'], "selected-country": auth.selectedCountry || "all" }
        }).then(() => {
            getAccessLinks();
        }).catch((err: any) => {
            console.log(err);
            alert(t("selectedopenbreeding.accesslinks.deleteerror"));
        });
    }

    function sendEditLinkByEmail(link: any) {
        if (!auth.selectedBreed) return;
        setSendingEmailGuid(link.ACCESSKEY);
        axios.single({
            method: 'post',
            url: `/api/dogs/puppylisting/accesslink/sendemail/${link.ACCESSKEY}/${auth.selectedBreed}`,
            responseType: 'json',
            validateStatus: () => true,
            headers: { 'x-access-token': auth.user['token'], "selected-country": auth.selectedCountry || "all" },
            data: { email: emailInputs[link.ACCESSKEY] || "", damName: link.DAM_NAME || null }
        }).then((res: any) => {
            if (res.status >= 400) {
                alert(t("selectedopenbreeding.accesslinks.sendemailerror"));
            } else {
                alert(t("selectedopenbreeding.accesslinks.sentemailsuccess"));
            }
        }).catch(() => {
            alert(t("selectedopenbreeding.accesslinks.sendemailerror"));
        }).finally(() => {
            setSendingEmailGuid(null);
        });
    }

    function copyAccessLink(guid: string) {
        const url = `${window.location.origin}/redigervalpeliste/${guid}`;
        navigator.clipboard.writeText(url).then(() => {
            alert(t("selectedopenbreeding.accesslinks.copied"));
        }).catch(() => {
            alert(t("selectedopenbreeding.accesslinks.copyerror"));
        });
    }

    function runBreedingSimulation(maleAnimalId: any, femaleAnimalId: any) {
        setIsSimulating(true);
        setSimulationResult(null);
        axios.single({
            method: 'post',
            url: "/api/dogs/simulatebreeding",
            responseType: 'json',
            data: {
                dogId: maleAnimalId,
                partnerIds: [femaleAnimalId]
            },
            headers: { 'x-access-token': auth.user['token'], "selected-country": auth.selectedCountry || "all" }
        }).then((res: any) => {
            if (res.data?.length > 0) {
                setSimulationResult(res.data[0]);
            }
        }).catch((err: any) => {
            console.log(err);
        }).finally(() => {
            setIsSimulating(false);
        });
    }
    
    useEffect(() => {
        if (
            (
                auth.user?.userdata?.ROLE != "admin" &&
                auth.user?.userdata?.ROLE != "sysadmin" &&
                auth.user?.userdata?.ROLE != "user"
            ) ||
            general.getBreedAccess(auth.user, auth.selectedBreed) == "dogowner"
        ) {
            navigate("/");
        }
    }, [auth.user?.userdata?.ROLE, auth.selectedBreed]);

    useEffect(() => {
        if (params.breedingid != undefined) {
          getBreeding();
        }
    }, [params.breedingid]);

    useEffect(() => {
        if (breeding?.ID) {
            getAccessLinks();
        }
    }, [breeding?.ID]);

    useEffect(() => {
        if (sire?.ANIMAL_ID != undefined && dam?.ANIMAL_ID != undefined) {
            runBreedingSimulation(sire.ANIMAL_ID, dam.ANIMAL_ID);
        } else {
            setSimulationResult(null);
        }
    }, [sire?.ANIMAL_ID, dam?.ANIMAL_ID]);

    const damPedigree = usePedigree(dam?.ANIMAL_ID, familyTreeGenerations);
    const sirePedigree = usePedigree(sire?.ANIMAL_ID, familyTreeGenerations);

    useEffect(() => {
        // Parse EXPECTING_DATE when breeding data is loaded
        if (breeding?.EXPECTING_DATE) {
            const parts = breeding.EXPECTING_DATE.split('.');
            if (parts.length === 2) {
                setExpectingMonth(parts[0] || "");
                setExpectingYear(parts[1] || "");
            }
        } else {
            setExpectingMonth("");
            setExpectingYear("");
        }
    }, [breeding?.EXPECTING_DATE]);

    useEffect(() => {
        // Update EXPECTING_DATE when month or year changes
        if (expectingMonth && expectingYear) {
            const newExpectingDate = `${expectingMonth}.${expectingYear}`;
            if (breeding?.EXPECTING_DATE !== newExpectingDate) {
                setBreeding({...breeding, EXPECTING_DATE: newExpectingDate});
            }
        } else if (!expectingMonth && !expectingYear && breeding?.EXPECTING_DATE) {
            setBreeding({...breeding, EXPECTING_DATE: ""});
        }
    }, [expectingMonth, expectingYear]);

    return (
        <div className="maincontent">
            <div className={styles.selectedopenbreeding}>
                <h1>{t("selectedopenbreeding.title")}</h1>
                <hr/>
                <div className={styles.actions}>
                    <button className="btn btn-primary" onClick={() => navigate("/partnersoknader")}>
                        {t("selectedopenbreeding.buttons.back")}
                    </button>
                    {breeding?.STATUS !== "disabled" && (
                        <button
                            className="btn btn-danger"
                            onClick={() => {
                                if (window.confirm(t("selectedopenbreeding.status.fields.confirmdisable"))) {
                                    setOpenBreedingStatus("disabled");
                                }
                            }}
                        >
                            {t("selectedopenbreeding.status.fields.disable")}
                        </button>
                    )}
                </div>
    
                <hr/>
                {breeding?.STATUS === "disabled" && (
                    <div className={styles.disabledbanner}>
                        <span>{t("selectedopenbreeding.disabledbanner")}</span>
                        <button
                            className={"btn btn-light " + styles.reenablebutton}
                            onClick={() => {
                                if (window.confirm(t("selectedopenbreeding.status.fields.confirmreenable"))) {
                                    setOpenBreedingStatus("awaiting");
                                }
                            }}
                        >
                            {t("selectedopenbreeding.status.fields.reenable")}
                        </button>
                    </div>
                )}
                <Container className={styles.container}>
                    <Row className={styles.row}>
                        <Col
                            xs="12"
                            sm="12"
                            md="12"
                            lg="12"
                            xl="12"
                            xxl="12"
                            className={styles.col}
                        >
                            <Card
                                title={t("selectedopenbreeding.familytree.title")}
                                children={<div>
                                    <PedigreeCard
                                        dam={damPedigree.data}
                                        sire={sirePedigree.data}
                                        damId={dam?.ANIMAL_ID}
                                        sireId={sire?.ANIMAL_ID}
                                        loading={damPedigree.loading || sirePedigree.loading}
                                        generations={familyTreeGenerations}
                                        onGenerationsChange={setFamilyTreeGenerations}
                                    />
                                </div>}
                            ></Card>
                        </Col>
                        <Col
                            sm="12"
                            md="12"
                            lg="6"
                            xl="6"
                            xxl="6"
                            className={styles.col}
                        >
                            <div className={styles.dogcardwrapper}>
                                <div
                                    className={styles.dogavatar}
                                    style={dam?.ANIMAL_ID ? { cursor: 'pointer' } : undefined}
                                    onClick={dam?.ANIMAL_ID ? () => window.open("/hunder/" + dam.ANIMAL_ID, "_blank") : undefined}
                                >
                                    <span className={styles.dogavatarplaceholder}>
                                        {(dam?.NAME ?? "?")[0]}
                                    </span>
                                    {damDog?.ANIMAL_ID && (
                                        <img
                                            src={`/api/dogs/photo/${damDog.ANIMAL_ID}`}
                                            alt=""
                                            onError={(e) => { (e.target as HTMLImageElement).style.display = 'none'; }}
                                        />
                                    )}
                                </div>
                                <Card
                                    title={
                                        dam?.ANIMAL_ID
                                            ? <span
                                                style={{ cursor: 'pointer', textDecoration: 'underline' }}
                                                onClick={() => window.open("/hunder/" + dam.ANIMAL_ID, "_blank")}
                                              >{t("selectedopenbreeding.dam.title")}</span>
                                            : t("selectedopenbreeding.dam.title")
                                    }
                                    children={<div>
                                        <hr/>
                                        <div className={styles.fieldlist}>
                                            <div className={styles.fielditem}>
                                                <span className={styles.fieldlabel}>{t("selectedopenbreeding.dam.fields.name")}</span>
                                                <div className={styles.fieldvaluerow}>
                                                    <span className={styles.fieldvalue}>{dam?.NAME}</span>
                                                    <button
                                                        className={styles.inlinecopybutton + (copiedKey === "dam-name" ? " " + styles.copied : "")}
                                                        onClick={() => copyToClipboard(dam?.NAME, "dam-name")}
                                                        title="Copy"
                                                    >{copiedKey === "dam-name" ? "✓" : "⎘"}</button>
                                                </div>
                                            </div>
                                            {damDog?.REGNO && (
                                                <div className={styles.fielditem}>
                                                    <span className={styles.fieldlabel}>{t("dogs.table.regno")}</span>
                                                    <div className={styles.fieldvaluerow}>
                                                        <span className={styles.fieldvalue}>{damDog.REGNO}</span>
                                                        <button
                                                            className={styles.inlinecopybutton + (copiedKey === "dam-regno" ? " " + styles.copied : "")}
                                                            onClick={() => copyToClipboard(damDog.REGNO, "dam-regno")}
                                                            title="Copy"
                                                        >{copiedKey === "dam-regno" ? "✓" : "⎘"}</button>
                                                    </div>
                                                </div>
                                            )}
                                            {damDog?.DATE_BIRTH && (
                                                <div className={styles.fielditem}>
                                                    <span className={styles.fieldlabel}>{t("dogs.table.birth")}</span>
                                                    <span className={styles.fieldvalue}>{getDogAge(damDog.DATE_BIRTH)}</span>
                                                </div>
                                            )}
                                            {damDog?.CHIP && (
                                                <div className={styles.fielditem}>
                                                    <span className={styles.fieldlabel}>{t("dogs.table.chip")}</span>
                                                    <div className={styles.fieldvaluerow}>
                                                        <span className={styles.fieldvalue}>{damDog.CHIP}</span>
                                                        <button
                                                            className={styles.inlinecopybutton + (copiedKey === "dam-chip" ? " " + styles.copied : "")}
                                                            onClick={() => copyToClipboard(damDog.CHIP, "dam-chip")}
                                                            title="Copy"
                                                        >{copiedKey === "dam-chip" ? "✓" : "⎘"}</button>
                                                    </div>
                                                </div>
                                            )}
                                            {damHdScore != null && (
                                                <div className={styles.fielditem}>
                                                    <span className={styles.fieldlabel}>{t("dog.statistics.hd.title")}</span>
                                                    <span className={styles.fieldvalue}>{damHdScore}</span>
                                                </div>
                                            )}
                                            {damUseLeash != null && damUseLoose != null && damUseHare != null /*&& damUseBird != null && damUseBirdfetch != null*/ && (
                                                <div className={styles.fielditem}>
                                                    <span className={styles.fieldlabel}>{t("dog.statistics.areas.use.numtests_total")}</span>
                                                    <span className={styles.fieldvalue}>{damUseLeash + damUseLoose + damUseHare /*+ damUseBird + damUseBirdfetch*/}</span>
                                                </div>
                                            )}
                                            {damPupsCount != null && (
                                                <div className={styles.fielditem}>
                                                    <span className={styles.fieldlabel}>{t("breedingpageconfig.publiccolumns.titles.ANTALL_AVKOM")}</span>
                                                    <span className={styles.fieldvalue}>{damPupsCount}</span>
                                                </div>
                                            )}
                                        </div>
                                    </div>}
                                ></Card>
                            </div>
                        </Col>
                        <Col
                            sm="12"
                            md="12"
                            lg="6"
                            xl="6"
                            xxl="6"
                            className={styles.col}
                        >
                            <div className={styles.dogcardwrapper}>
                                <div
                                    className={styles.dogavatar}
                                    style={sire?.ANIMAL_ID ? { cursor: 'pointer' } : undefined}
                                    onClick={sire?.ANIMAL_ID ? () => window.open("/hunder/" + sire.ANIMAL_ID, "_blank") : undefined}
                                >
                                    <span className={styles.dogavatarplaceholder}>
                                        {(sire?.NAME ?? "?")[0]}
                                    </span>
                                    {sireDog?.ANIMAL_ID && (
                                        <img
                                            src={`/api/dogs/photo/${sireDog.ANIMAL_ID}`}
                                            alt=""
                                            onError={(e) => { (e.target as HTMLImageElement).style.display = 'none'; }}
                                        />
                                    )}
                                </div>
                                <Card
                                    title={
                                        sire?.ANIMAL_ID
                                            ? <span
                                                style={{ cursor: 'pointer', textDecoration: 'underline' }}
                                                onClick={() => window.open("/hunder/" + sire.ANIMAL_ID, "_blank")}
                                              >{t("selectedopenbreeding.sire.title")}</span>
                                            : t("selectedopenbreeding.sire.title")
                                    }
                                    children={<div>
                                        <hr/>
                                        {sire != undefined ?
                                            <>
                                                <div className={styles.fieldlist}>
                                                    <div className={styles.fielditem}>
                                                        <span className={styles.fieldlabel}>{t("selectedopenbreeding.sire.fields.name")}</span>
                                                        <div className={styles.fieldvaluerow}>
                                                            <span className={styles.fieldvalue}>{sire?.NAME}</span>
                                                            <button
                                                                className={styles.inlinecopybutton + (copiedKey === "sire-name" ? " " + styles.copied : "")}
                                                                onClick={() => copyToClipboard(sire?.NAME, "sire-name")}
                                                                title="Copy"
                                                            >{copiedKey === "sire-name" ? "✓" : "⎘"}</button>
                                                        </div>
                                                    </div>
                                                    {sireDog?.REGNO && (
                                                        <div className={styles.fielditem}>
                                                            <span className={styles.fieldlabel}>{t("dogs.table.regno")}</span>
                                                            <div className={styles.fieldvaluerow}>
                                                                <span className={styles.fieldvalue}>{sireDog.REGNO}</span>
                                                                <button
                                                                    className={styles.inlinecopybutton + (copiedKey === "sire-regno" ? " " + styles.copied : "")}
                                                                    onClick={() => copyToClipboard(sireDog.REGNO, "sire-regno")}
                                                                    title="Copy"
                                                                >{copiedKey === "sire-regno" ? "✓" : "⎘"}</button>
                                                            </div>
                                                        </div>
                                                    )}
                                                    {sireDog?.DATE_BIRTH && (
                                                        <div className={styles.fielditem}>
                                                            <span className={styles.fieldlabel}>{t("dogs.table.birth")}</span>
                                                            <span className={styles.fieldvalue}>{getDogAge(sireDog.DATE_BIRTH)}</span>
                                                        </div>
                                                    )}
                                                    {sireDog?.CHIP && (
                                                        <div className={styles.fielditem}>
                                                            <span className={styles.fieldlabel}>{t("dogs.table.chip")}</span>
                                                            <div className={styles.fieldvaluerow}>
                                                                <span className={styles.fieldvalue}>{sireDog.CHIP}</span>
                                                                <button
                                                                    className={styles.inlinecopybutton + (copiedKey === "sire-chip" ? " " + styles.copied : "")}
                                                                    onClick={() => copyToClipboard(sireDog.CHIP, "sire-chip")}
                                                                    title="Copy"
                                                                >{copiedKey === "sire-chip" ? "✓" : "⎘"}</button>
                                                            </div>
                                                        </div>
                                                    )}
                                                    {sireHdScore != null && (
                                                        <div className={styles.fielditem}>
                                                            <span className={styles.fieldlabel}>{t("dog.statistics.hd.title")}</span>
                                                            <span className={styles.fieldvalue}>{sireHdScore}</span>
                                                        </div>
                                                    )}
                                                    {sireUseLeash != null && sireUseLoose != null && sireUseHare != null && /*sireUseBird != null && sireUseBirdfetch != null*/ (
                                                        <div className={styles.fielditem}>
                                                            <span className={styles.fieldlabel}>{t("dog.statistics.areas.use.numtests_total")}</span>
                                                            <span className={styles.fieldvalue}>{sireUseLeash + sireUseLoose + sireUseHare /*+ sireUseBird + sireUseBirdfetch*/}</span>
                                                        </div>
                                                    )}
                                                    {sirePupsCount != null && (
                                                        <div className={styles.fielditem}>
                                                            <span className={styles.fieldlabel}>{t("breedingpageconfig.publiccolumns.titles.ANTALL_AVKOM")}</span>
                                                            <span className={styles.fieldvalue}>{sirePupsCount}</span>
                                                        </div>
                                                    )}
                                                </div>
                                                <button
                                                    className={"btn btn-primary " + styles.removesirebutton}
                                                    onClick={() => {
                                                        addSire(undefined);
                                                    }}
                                                >
                                                    {t("selectedopenbreeding.sire.removesire")}
                                                </button>
                                            </>
                                        :
                                            <div>
                                                <p>{t("selectedopenbreeding.sire.nosire")}</p>
                                                <hr/>
                                                <div>
                                                    <div className={styles.searchsire}>
                                                        <input
                                                            type="text"
                                                            className="form-control"
                                                            value={searchText}
                                                            onChange={(e) => setSearchText(e.target.value)}
                                                            onKeyPress={(e) => {
                                                                if (e.key == "Enter" && searchText != "" && !loading) {
                                                                    searchSingleSire();
                                                                }
                                                            }}
                                                            placeholder={t("selectedopenbreeding.sire.searchsireplaceholder")}
                                                        />
                                                        <button
                                                            className="btn btn-primary"
                                                            disabled={searchText == "" || loading}
                                                            onClick={() => {
                                                                searchSingleSire();
                                                            }}
                                                        >
                                                            {loading ? t("selectedopenbreeding.sire.searching") : t("selectedopenbreeding.sire.searchsire")}
                                                        </button>
                                                    </div>
                                                    <hr/>
                                                    <div className={styles.searchsireresults}>
                                                        {searchResults?.length > 0 ?
                                                            <>
                                                            {searchResults?.length >= 50 && <p style={{ color: 'orange', marginTop: 0, marginBottom: '8px' }}>{t("shared.search.maxresults")}</p>}
                                                            <table className={styles.generaltable}>
                                                                <tbody>
                                                                    {searchResults?.map((dog: any, index: number) => {
                                                                        return (
                                                                            <tr key={index}>
                                                                                <td>
                                                                                    {dog.NAME}
                                                                                </td>
                                                                                <td>
                                                                                    <button
                                                                                        className={"btn btn-primary " + styles.addsirebutton}
                                                                                        disabled={addingSire}
                                                                                        onClick={() => {
                                                                                            addSire(dog);
                                                                                        }}
                                                                                    >
                                                                                        {addingSire ? t("selectedopenbreeding.sire.addingsire") : t("selectedopenbreeding.sire.addsire")}
                                                                                    </button>
                                                                                </td>
                                                                            </tr>
                                                                        );
                                                                    })}
                                                                    <tr>
                                                                    </tr>
                                                                </tbody>
                                                            </table>
                                                            {searchResults?.length >= 50 && <p style={{ color: 'orange', marginTop: '8px', marginBottom: 0 }}>{t("shared.search.maxresults")}</p>}
                                                            </>
                                                        : null}
                                                    </div>
                                                </div>
                                            </div>
                                        }
                                    </div>}
                                ></Card>
                            </div>
                        </Col>
                        {sire?.ANIMAL_ID != undefined && dam?.ANIMAL_ID != undefined && (
                            <Col
                                xs="12"
                                sm="12"
                                md="12"
                                lg="12"
                                xl="12"
                                xxl="12"
                                className={styles.col}
                            >
                                <Card
                                    title={t("selectedopenbreeding.simulation.title")}
                                    children={<div>
                                        <hr/>
                                        {isSimulating ? (
                                            <p>{t("selectedopenbreeding.simulation.loading")}</p>
                                        ) : (
                                            (() => {
                                                const disabledColumns: string[] = (puppyListingColumns ?? [])
                                                    .filter((col: any) => col.PBC_STATUS === 'disabled')
                                                    .map((col: any) => col.PBC_COLUMN_IDENTIFIER as string);
                                                const columnLabelMap: Record<string, string> = {};
                                                (puppyListingColumns ?? []).forEach((col: any) => {
                                                    if (col.PBC_COLUMN_IDENTIFIER && col.PBCI_LABEL) {
                                                        columnLabelMap[col.PBC_COLUMN_IDENTIFIER] = col.PBCI_LABEL;
                                                    }
                                                });
                                                const getColumnLabel = (key: string): string =>
                                                    columnLabelMap[key] ?? t("breedsimulator.results.traits." + key);
                                                const isColumnDisabled = (key: string) =>
                                                    disabledColumns.some(col => key === col || key.startsWith(col + '_'));
                                                const entries: [string, any][] = simulationResult?.offspring != null
                                                    ? (Object.entries(simulationResult.offspring) as [string, any][])
                                                        .filter(([key, val]) =>
                                                            (typeof val === 'string' && val.startsWith('SPACER|')) ||
                                                            (val != null && !key.endsWith('_HASNULL') && !(simulationResult.offspring[key + '_HASNULL'] > 0) &&
                                                                !(['GLAUKOM', 'ATAKSI', 'DWARF'].includes(key.toUpperCase()) && val === 'ikke testet/ikke testet') &&
                                                                !isColumnDisabled(key))
                                                        )
                                                        .filter(([, val], index, arr) => {
                                                            if (!(typeof val === 'string' && val.startsWith('SPACER|'))) return true;
                                                            for (let i = index + 1; i < arr.length; i++) {
                                                                const nextVal = arr[i][1];
                                                                if (typeof nextVal === 'string' && nextVal.startsWith('SPACER|')) break;
                                                                return true;
                                                            }
                                                            return false;
                                                        })
                                                    : [];
                                                const groups: { title: string; fields: [string, any][] }[] = [];
                                                let current: { title: string; fields: [string, any][] } | null = null;
                                                entries.forEach(([key, val]) => {
                                                    if (typeof val === 'string' && val.startsWith('SPACER|')) {
                                                        if (current) groups.push(current);
                                                        current = { title: val.slice(7), fields: [] };
                                                    } else {
                                                        if (!current) current = { title: '', fields: [] };
                                                        current.fields.push([key, val]);
                                                    }
                                                });
                                                if (current) groups.push(current);
                                                return groups.map((group, gi) => {
                                                    const ebvFields = group.fields.filter(
                                                        ([key, val]) => key.toUpperCase().includes('EBV') && val !== null && val !== '' && !isNaN(Number(val))
                                                    );
                                                    const showChart = ebvFields.length >= 3;
                                                    const radarData = {
                                                        labels: ebvFields.map(([key]) => getColumnLabel(key)),
                                                        datasets: [
                                                            {
                                                                label: 'Baseline',
                                                                data: ebvFields.map(() => 200),
                                                                backgroundColor: 'rgba(0,0,0,0)',
                                                                borderColor: 'rgba(180, 180, 180, 0.7)',
                                                                borderWidth: 1,
                                                                borderDash: [4, 3],
                                                                pointRadius: 0,
                                                            },
                                                            {
                                                                label: group.title,
                                                                data: ebvFields.map(([, val]) => Number(val)),
                                                                backgroundColor: 'rgba(54, 162, 235, 0.15)',
                                                                borderColor: 'rgba(54, 162, 235, 0.8)',
                                                                borderWidth: 1.5,
                                                                pointBackgroundColor: 'rgba(54, 162, 235, 1)',
                                                            }
                                                        ]
                                                    };
                                                    const radarOptions = {
                                                        responsive: true,
                                                        maintainAspectRatio: false,
                                                        plugins: { legend: { display: false } },
                                                        scales: {
                                                            r: {
                                                                min: 150,
                                                                max: 250,
                                                                ticks: { display: false, stepSize: 50 },
                                                                pointLabels: { display: false },
                                                            }
                                                        }
                                                    };
                                                    return (
                                                        <div key={gi} className={styles.simgroup}>
                                                            {group.title && <div className={styles.fieldcategory}>{group.title}</div>}
                                                            <div className={styles.simgroupcontent}>
                                                                <div className={styles.fieldlistgrid}>
                                                                    {group.fields.map(([key, val], fi) => (
                                                                        <div key={fi} className={styles.simitem}>
                                                                            <span className={styles.simlabel}>{getColumnLabel(key)}</span>
                                                                            <div className={styles.fieldvaluerow}>
                                                                                <span className={styles.simvalue}>{String(val)}</span>
                                                                                <button
                                                                                    className={styles.inlinecopybutton + (copiedKey === `sim-${key}` ? " " + styles.copied : "")}
                                                                                    onClick={() => copyToClipboard(String(val), `sim-${key}`)}
                                                                                    title="Copy"
                                                                                >{copiedKey === `sim-${key}` ? "✓" : "⎘"}</button>
                                                                            </div>
                                                                        </div>
                                                                    ))}
                                                                </div>
                                                                {showChart && (
                                                                    <div className={styles.simchart}>
                                                                        <Radar data={radarData} options={radarOptions} />
                                                                    </div>
                                                                )}
                                                            </div>
                                                        </div>
                                                    );
                                                });
                                            })()
                                        )}
                                    </div>}
                                ></Card>
                            </Col>
                        )}
                        <Col
                            sm="12"
                            md="12"
                            lg="6"
                            xl="6"
                            xxl="6"
                            className={styles.col}
                        >
                            &nbsp;
                            <Card
                                title={t("selectedopenbreeding.status.title")}
                                children={<div>
                                    <hr/>
                                    <div className={styles.fieldlist}>
                                        <div className={styles.fielditem}>
                                            <span className={styles.fieldlabel}>{t("selectedopenbreeding.status.fields.expectingdate")}</span>
                                            <div style={{ display: 'flex', gap: '10px' }}>
                                                <Form.Control
                                                    as="select"
                                                    value={expectingMonth}
                                                    onChange={(e: any) => setExpectingMonth(e.target.value)}
                                                    style={{ flex: 1 }}
                                                >
                                                    <option value="">{t('editpuppylisting.form.month')}</option>
                                                    <option value="01">{t('editpuppylisting.form.months.january')}</option>
                                                    <option value="02">{t('editpuppylisting.form.months.february')}</option>
                                                    <option value="03">{t('editpuppylisting.form.months.march')}</option>
                                                    <option value="04">{t('editpuppylisting.form.months.april')}</option>
                                                    <option value="05">{t('editpuppylisting.form.months.may')}</option>
                                                    <option value="06">{t('editpuppylisting.form.months.june')}</option>
                                                    <option value="07">{t('editpuppylisting.form.months.july')}</option>
                                                    <option value="08">{t('editpuppylisting.form.months.august')}</option>
                                                    <option value="09">{t('editpuppylisting.form.months.september')}</option>
                                                    <option value="10">{t('editpuppylisting.form.months.october')}</option>
                                                    <option value="11">{t('editpuppylisting.form.months.november')}</option>
                                                    <option value="12">{t('editpuppylisting.form.months.december')}</option>
                                                </Form.Control>
                                                <Form.Control
                                                    as="select"
                                                    value={expectingYear}
                                                    onChange={(e: any) => setExpectingYear(e.target.value)}
                                                    style={{ flex: 1 }}
                                                >
                                                    <option value="">{t('editpuppylisting.form.year')}</option>
                                                    {Array.from({ length: 5 }, (_, i) => new Date().getFullYear() - 1 + i).map(year => (
                                                        <option key={year} value={year}>{year}</option>
                                                    ))}
                                                </Form.Control>
                                            </div>
                                        </div>
                                        <div className={styles.fielditem}>
                                            <span className={styles.fieldlabel}>{t("selectedopenbreeding.status.fields.birthdate")}</span>
                                            <input
                                                className={styles.expectingdateinput}
                                                type="text"
                                                onChange={(e) => {
                                                    setBreeding({...breeding, BIRTHDATE: e.target.value});
                                                }}
                                                value={breeding?.BIRTHDATE}
                                            />
                                        </div>
                                        <div className={styles.fielditem}>
                                            <span className={styles.fieldlabel}>{t("selectedopenbreeding.status.fields.malepups")}</span>
                                            <input
                                                className={styles.expectingdateinput}
                                                type="number"
                                                min="0"
                                                onChange={(e) => {
                                                    setBreeding({...breeding, MALEPUPS: e.target.value});
                                                }}
                                                value={breeding?.MALEPUPS || ''}
                                            />
                                        </div>
                                        <div className={styles.fielditem}>
                                            <span className={styles.fieldlabel}>{t("selectedopenbreeding.status.fields.femalepups")}</span>
                                            <input
                                                className={styles.expectingdateinput}
                                                type="number"
                                                min="0"
                                                onChange={(e) => {
                                                    setBreeding({...breeding, FEMALEPUPS: e.target.value});
                                                }}
                                                value={breeding?.FEMALEPUPS || ''}
                                            />
                                        </div>
                                        <div className={styles.fielditem}>
                                            <span className={styles.fieldlabel}>{t("selectedopenbreeding.status.fields.freetext")}</span>
                                            <textarea
                                                className={styles.expectingdateinput}
                                                rows={3}
                                                onChange={(e) => {
                                                    setBreeding({...breeding, FREETEXT: e.target.value});
                                                }}
                                                value={breeding?.FREETEXT || ''}
                                            />
                                        </div>
                                        <div className={styles.fielditem}>
                                            <span className={styles.fieldlabel}>{t("selectedopenbreeding.status.fields.kennelname")}</span>
                                            <input
                                                className={styles.expectingdateinput}
                                                type="text"
                                                onChange={(e) => {
                                                    setBreeding({...breeding, KENNEL_NAME: e.target.value});
                                                }}
                                                value={breeding?.KENNEL_NAME || ''}
                                            />
                                        </div>
                                        <div className={styles.fielditem}>
                                            <span className={styles.fieldlabel}>{t("selectedopenbreeding.status.fields.status")}</span>
                                            <Form.Control
                                                as="select"
                                                value={breeding?.STATUS}
                                                onChange={(e: any) => {
                                                    setBreeding({...breeding, STATUS: e.target.value});
                                                }}
                                            >
                                                <option value="awaiting">{t("selectedopenbreeding.status.fields.awaiting")}</option>
                                                <option value="mated">{t("selectedopenbreeding.status.fields.mated")}</option>
                                                <option value="attempted">{t("selectedopenbreeding.status.fields.attempted")}</option>
                                                <option value="failed">{t("selectedopenbreeding.status.fields.failed")}</option>
                                                <option value="successful">{t("selectedopenbreeding.status.fields.successful")}</option>
                                                {breeding?.STATUS === "disabled" && (
                                                    <option value="disabled" disabled>{t("selectedopenbreeding.status.fields.disabled")}</option>
                                                )}
                                            </Form.Control>
                                        </div>
                                    </div>
                                    <hr/>
                                    <button
                                        className={"btn btn-primary " + styles.savebutton}
                                        onClick={() => {
                                            updateBreeding();
                                        }}
                                    >
                                        {t("selectedopenbreeding.status.fields.save")}
                                    </button>
                                </div>}
                            ></Card>
                            &nbsp;
                            <Card
                                title={t("selectedopenbreeding.damapplication.title")}
                                children={<div>
                                    <hr/>
                                    <div className={styles.fieldlist}>
                                        {selectedReg?.map((field: any, index: number) => (
                                            <div key={index} className={styles.fielditem}>
                                                <span className={styles.fieldlabel}>{field.TEXT}</span>
                                                <div className={styles.fieldvaluerow}>
                                                    <span className={styles.fieldvalue}>{field.answer}</span>
                                                    <button
                                                        className={styles.inlinecopybutton + (copiedKey === `data-${index}` ? " " + styles.copied : "")}
                                                        onClick={() => copyToClipboard(field.answer, `data-${index}`)}
                                                        title="Copy"
                                                    >{copiedKey === `data-${index}` ? "✓" : "⎘"}</button>
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                </div>}
                            ></Card>
                        </Col>
                        <Col
                            sm="12"
                            md="12"
                            lg="6"
                            xl="6"
                            xxl="6"
                            className={styles.col}
                        >
                            &nbsp;
                            <Card
                                title={t("selectedopenbreeding.accesslinks.title")}
                                children={<div>
                                    <hr/>
                                    <p>{t("selectedopenbreeding.accesslinks.description")}</p>
                                    <button
                                        className={"btn btn-primary " + styles.generatebutton}
                                        onClick={generateAccessLink}
                                        disabled={generatingLink}
                                    >
                                        {generatingLink ? t("selectedopenbreeding.accesslinks.generating") : t("selectedopenbreeding.accesslinks.generate")}
                                    </button>
                                    <hr/>
                                    {loadingLinks ? (
                                        <p>{t("selectedopenbreeding.accesslinks.loading")}</p>
                                    ) : accessLinks.length > 0 ? (
                                        <div className={styles.accesslinkslist}>
                                            {accessLinks.map((link: any, index: number) => (
                                                <div key={index} className={styles.accesslinkitem}>
                                                    <div className={styles.linkinfo}>
                                                        <span className={styles.linkstatus}>
                                                            {link.STATUS === 'active' ? '🟢' : '🔴'}
                                                        </span>
                                                        <div className={styles.linkdetails}>
                                                            <small>
                                                                <strong>{t("selectedopenbreeding.accesslinks.created")}: {new Date(link.CREATED_DATE).toLocaleString("no-NO")}</strong>
                                                            </small>
                                                            {link.LAST_USED_DATE && (
                                                                <small>
                                                                    {t("selectedopenbreeding.accesslinks.lastused")}: {new Date(link.LAST_USED_DATE).toLocaleString("no-NO")}
                                                                </small>
                                                            )}
                                                            <small>
                                                                ...{link.ACCESSKEY.slice(-8)}
                                                            </small>
                                                        </div>
                                                    </div>
                                                    <div className={styles.linkactions}>
                                                        <div className={styles.linkactionbuttons}>
                                                        <button
                                                            className="btn btn-sm btn-primary"
                                                            onClick={() => copyAccessLink(link.ACCESSKEY)}
                                                        >
                                                            {t("selectedopenbreeding.accesslinks.copy")}
                                                        </button>
                                                        {link.STATUS === 'active' ? (
                                                            <>
                                                            <button
                                                                className={"btn btn-sm btn-warning " + styles.deactivatebutton}
                                                                onClick={() => deactivateAccessLink(link.ACCESSKEY)}
                                                            >
                                                                {t("selectedopenbreeding.accesslinks.deactivate")}
                                                            </button>
                                                            </>
                                                        ) : (
                                                            <>
                                                                <button
                                                                    className={"btn btn-sm btn-success " + styles.activatebutton}
                                                                    onClick={() => activateAccessLink(link.ACCESSKEY)}
                                                                >
                                                                    {t("selectedopenbreeding.accesslinks.activate")}
                                                                </button>
                                                                <button
                                                                    className={"btn btn-sm btn-danger " + styles.deletebutton}
                                                                    onClick={() => hardDeleteAccessLink(link.ACCESSKEY)}
                                                                >
                                                                    {t("selectedopenbreeding.accesslinks.delete")}
                                                                </button>
                                                            </>
                                                        )}
                                                        </div>
                                                        {link.STATUS === 'active' && (
                                                            <div className={styles.emailinputrow}>
                                                                <input
                                                                    type="email"
                                                                    className="form-control form-control-sm"
                                                                    value={emailInputs[link.ACCESSKEY] ?? ""}
                                                                    onChange={(e) => setEmailInputs(prev => ({ ...prev, [link.ACCESSKEY]: e.target.value }))}
                                                                    placeholder="E-postadresse"
                                                                />
                                                                <button
                                                                    className="btn btn-sm btn-success"
                                                                    disabled={sendingEmailGuid === link.ACCESSKEY || !emailInputs[link.ACCESSKEY]}
                                                                    onClick={() => sendEditLinkByEmail(link)}
                                                                >
                                                                    {sendingEmailGuid === link.ACCESSKEY
                                                                        ? t("selectedopenbreeding.accesslinks.sendingemail")
                                                                        : t("selectedopenbreeding.accesslinks.sendemail")}
                                                                </button>
                                                            </div>
                                                        )}
                                                    </div>
                                                </div>
                                            ))}
                                        </div>
                                    ) : (
                                        <p>{t("selectedopenbreeding.accesslinks.nolinks")}</p>
                                    )}
                                </div>}
                            ></Card>
                            &nbsp;
                            <Card
                                title={t("selectedopenbreeding.public.title")}
                            >
                                <hr/>
                                <div className={styles.fieldlist}>
                                    <div className={styles.fielditem}>
                                        <span className={styles.fieldlabel}>{t("selectedopenbreeding.public.published")}</span>
                                        <div className={styles.fieldvaluerow}>
                                            <span className={styles.fieldvalue}>
                                                {breeding?.PUBLISHED_PUBLIC != "0" && breeding?.PUBLISHED_PUBLIC != null ?
                                                    t("selectedopenbreeding.public.true")
                                                :
                                                    t("selectedopenbreeding.public.false")
                                                }
                                            </span>
                                            <button
                                                className="btn btn-sm btn-primary"
                                                onClick={() => {
                                                    toggleApprovedPublic(breeding?.PUBLISHED_PUBLIC == "1" ? "0" : "1");
                                                }}
                                            >
                                                {breeding?.PUBLISHED_PUBLIC != "0" && breeding?.PUBLISHED_PUBLIC != null ?
                                                    t("selectedopenbreeding.public.unpublish")
                                                :
                                                    t("selectedopenbreeding.public.publish")
                                                }
                                            </button>
                                        </div>
                                    </div>
                                </div>
                            </Card>
                        </Col>
                    </Row>
                </Container>
            </div>
        </div>
    );
};

class SelectedOpenBreedingProps {
    
}

export default SelectedOpenBreeding;